import { Controller } from "@hotwired/stimulus"
import confetti from "canvas-confetti"

export default class extends Controller {
  static targets = ["displayMobile", "displayDesktop", "modalDuration", "modal", "stopButton", "deleteButton", "deleteModal", "notesField"]
  static values = { 
    startTime: String,
    id: Number
  }

  connect() {
    // Auto-resize notes field on load if content exists
    this.autoResizeNotesField()
    
    // Immediate start if we have start time
    if (this.startTimeValue) {
      this.startTimer()
    }
  }

  disconnect() {
    this.stopTimer()
  }

  startTimeValueChanged() {
    // If we don't have a running timer and we now have a start time, start the timer
    if (this.startTimeValue && !this.timerInterval) {
      this.startTimer()
    } else if (!this.startTimeValue) {
      this.stopTimer()
    }
  }

  startTimer() {
    // Stop any existing timer first
    this.stopTimer()
    
    // Update timer display immediately
    this.updateDisplay()
    
    // Set up interval to update every second
    this.timerInterval = setInterval(() => {
      this.updateDisplay()
    }, 1000)
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  updateDisplay() {
    if (!this.startTimeValue) {
      return
    }

    try {
      const startTime = new Date(this.startTimeValue)
      const now = new Date()
      
      // Ensure start time is valid
      if (isNaN(startTime.getTime())) {
        console.error('Invalid start time:', this.startTimeValue)
        return
      }
      
      const diffInSeconds = Math.floor((now - startTime) / 1000)
      
      // Ensure we don't show negative time
      const actualDiff = Math.max(0, diffInSeconds)
      
      // Calculate hours, minutes, and seconds
      const hours = Math.floor(actualDiff / 3600)
      const minutes = Math.floor((actualDiff % 3600) / 60)
      const seconds = actualDiff % 60
      
      // Format display with seconds
      let display
      if (hours > 0) {
        display = `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      } else {
        display = `${minutes}:${seconds.toString().padStart(2, '0')}`
      }
      
      // Update mobile display
      if (this.hasDisplayMobileTarget) {
        this.displayMobileTarget.textContent = display
      }
      
      // Update desktop display
      if (this.hasDisplayDesktopTarget) {
        this.displayDesktopTarget.textContent = display
      }
      
      // Update modal duration
      if (this.hasModalDurationTarget) {
        this.modalDurationTarget.textContent = display
      }

    } catch (error) {
      // Silently handle timer display errors
    }
  }

  showStopModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      // Focus on the notes textarea
      const notesField = this.modalTarget.querySelector('textarea[name="notes"]')
      if (notesField) {
        // Small delay to ensure modal is visible
        setTimeout(() => notesField.focus(), 100)
      }
    } else {
      this.fallbackStopModal()
    }
  }

  fallbackStopModal() {
    // Fallback: look for modal by ID as last resort
    const modal = document.getElementById('stopTimerModal')
    if (modal) {
      modal.classList.remove('hidden')
      const notesField = modal.querySelector('textarea[name="notes"]')
      if (notesField) {
        setTimeout(() => notesField.focus(), 100)
      }
    } else {
      // Last resort: redirect to stop timer page
      if (confirm('Stop the current timer?')) {
        const form = document.createElement('form')
        form.method = 'PATCH'
        form.action = '/time_logs/stop_timer'
        
        const csrfToken = document.querySelector('[name="csrf-token"]')?.content ||
                         document.querySelector('meta[name="csrf-token"]')?.content
        if (csrfToken) {
          const csrfInput = document.createElement('input')
          csrfInput.type = 'hidden'
          csrfInput.name = 'authenticity_token'
          csrfInput.value = csrfToken
          form.appendChild(csrfInput)
        }
        
        document.body.appendChild(form)
        form.submit()
      }
    }
  }

  hideStopModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
    } else {
      // Fallback: hide modal by ID
      const modal = document.getElementById('stopTimerModal')
      if (modal) {
        modal.classList.add('hidden')
      }
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }


  showDeleteConfirmation() {
    // Hide the stop modal first
    this.hideStopModal()
    
    // Show the delete confirmation modal
    if (this.hasDeleteModalTarget) {
      this.deleteModalTarget.classList.remove('hidden')
    } else {
      console.error('Delete modal target not found, falling back to browser confirm')
      // Fallback to browser confirm
      const confirmed = confirm(
        'Are you sure you want to delete this timer?\n\n' +
        'This will permanently delete the timer without saving any time log. ' +
        'This action cannot be undone.'
      )
      
      if (confirmed) {
        this.deleteTimer()
      }
    }
  }

  hideDeleteModal() {
    if (this.hasDeleteModalTarget) {
      this.deleteModalTarget.classList.add('hidden')
    } else {
      // Fallback: hide modal by ID
      const modal = document.getElementById('deleteTimerModal')
      if (modal) {
        modal.classList.add('hidden')
      }
    }
  }

  confirmDelete() {
    // Hide the delete modal
    this.hideDeleteModal()
    // Proceed with deletion
    this.deleteTimer()
  }

  deleteTimer() {
    if (!this.idValue) {
      console.error('No timer ID available for deletion')
      return
    }

    // Hide the modal immediately
    this.hideStopModal()

    // Create a form and submit it to delete the timer
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `/time_logs/${this.idValue}`

    // Add hidden method field for DELETE
    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'DELETE'
    form.appendChild(methodInput)

    // Add CSRF token
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content ||
                     document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Submit the form
    document.body.appendChild(form)
    form.submit()
  }

  // Auto-resize notes field on page load if content exists
  autoResizeNotesField() {
    if (this.hasNotesFieldTarget) {
      const textarea = this.notesFieldTarget
      if (textarea.value.trim().length > 0) {
        // Reset height to auto to calculate scroll height
        textarea.style.height = 'auto'
        // Set height to fit content, respecting max height
        textarea.style.height = Math.min(textarea.scrollHeight, 128) + 'px'
        console.log('Auto-resized notes field on load')
      }
    }
  }

  // Update timer notes with debounced auto-save
  updateNotes(event) {
    const notes = event.target.value
    
    // Clear existing timeout
    if (this.notesUpdateTimeout) {
      clearTimeout(this.notesUpdateTimeout)
    }
    
    // Debounce the update - wait 1 second after user stops typing
    this.notesUpdateTimeout = setTimeout(() => {
      this.saveNotes(notes)
    }, 1000)
  }

  saveNotes(notes) {
    if (!this.idValue) {
      console.error('No timer ID available for notes update')
      return
    }

    const csrfToken = document.querySelector('[name="csrf-token"]')?.content ||
                     document.querySelector('meta[name="csrf-token"]')?.content

    fetch('/time_logs/update_timer_notes', {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': csrfToken
      },
      body: new URLSearchParams({
        notes: notes
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      console.log('Notes updated successfully:', data)
    })
    .catch(error => {
      console.error('Error updating notes:', error)
    })
  }

  // Handle stop timer form submission with celebration
  handleStopSubmit(event) {
    event.preventDefault() // Prevent immediate form submission
    
    // Get the form data
    const form = event.target
    const formData = new FormData(form)
    
    // Close the modal first
    this.hideStopModal()
    
    // Trigger celebration animation immediately
    this.triggerCelebrationAndSubmit(form, formData)
  }
  
  triggerCelebrationAndSubmit(form, formData) {
    try {
      // Check if celebrations are enabled for the user
      this.isCelebrationsEnabled().then(celebrationsEnabled => {
        if (celebrationsEnabled) {
          // Get category for celebration color
          const categoryId = formData.get('category_id')
          
          // Calculate duration for celebration display
          const duration = this.calculateCurrentDuration()
          
          // Get category name and color (we'll need to fetch this or pass it from the modal)
          this.getCategoryInfo(categoryId).then(categoryInfo => {
            // Use simple celebration that works reliably
            this.showSimpleCelebration(categoryInfo, duration)
            
            // Submit form in background after celebration has had time to play
            setTimeout(() => {
              this.submitStopForm(form, formData)
            }, 2000) // Wait 2 seconds - enough to see celebration but not interrupt flow
          }).catch(error => {
            // If celebration fails, still submit the form
            console.warn('Celebration failed, submitting form normally:', error)
            this.submitStopForm(form, formData)
          })
        } else {
          // Skip celebration and submit immediately
          this.submitStopForm(form, formData)
        }
      }).catch(error => {
        // If we can't check the setting, default to showing celebration
        console.warn('Could not check celebrations setting, defaulting to enabled:', error)
        
        // Get category for celebration color
        const categoryId = formData.get('category_id')
        
        // Calculate duration for celebration display
        const duration = this.calculateCurrentDuration()
        
        // Get category name and color (we'll need to fetch this or pass it from the modal)
        this.getCategoryInfo(categoryId).then(categoryInfo => {
          // Use simple celebration that works reliably
          this.showSimpleCelebration(categoryInfo, duration)
          
          // Submit form in background after celebration has had time to play
          setTimeout(() => {
            this.submitStopForm(form, formData)
          }, 2000) // Wait 2 seconds - enough to see celebration but not interrupt flow
        }).catch(error => {
          // If celebration fails, still submit the form
          console.warn('Celebration failed, submitting form normally:', error)
          this.submitStopForm(form, formData)
        })
      })
    } catch (error) {
      // If celebration fails, still submit the form
      console.warn('Celebration failed, submitting form normally:', error)
      this.submitStopForm(form, formData)
    }
  }
  
  calculateCurrentDuration() {
    if (!this.startTimeValue) return "0s"
    
    const startTime = new Date(this.startTimeValue)
    const now = new Date()
    const diffInSeconds = Math.floor((now - startTime) / 1000)
    const hours = Math.floor(diffInSeconds / 3600)
    const minutes = Math.floor((diffInSeconds % 3600) / 60)
    const seconds = diffInSeconds % 60
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    } else {
      return `${seconds}s`
    }
  }
  
  async getCategoryInfo(categoryId) {
    // For now, we'll use a default. In a real implementation, you might fetch this
    // or pass the category data from the modal
    const categories = {
      // This should match your Category model COLOR_PALETTE
      colors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#F97316", "#84CC16", "#EC4899", "#6B7280"]
    }
    
    // Get category name from the modal dropdown
    const categoryInput = document.querySelector('[data-searchable-dropdown-target="searchInput"]')
    const categoryName = categoryInput?.value || "Work"
    
    // Calculate color based on category ID (same logic as backend)
    const colorIndex = categoryId ? (parseInt(categoryId) % categories.colors.length) : 0
    const color = categories.colors[colorIndex]
    
    return {
      name: categoryName,
      color: color
    }
  }
  
  
  showSimpleCelebration(categoryInfo, duration) {
    // Remove any existing celebration
    const existing = document.getElementById('timer-celebration')
    if (existing) existing.remove()
    
    // Create a simple celebration without Stimulus complications
    const celebrationHtml = `
      <div id="timer-celebration" 
           class="fixed inset-0 pointer-events-none z-[10000] flex items-center justify-center"
           style="background-color: rgba(0, 0, 0, 0.1);">
        
        <div class="text-center">
          <div class="text-white px-12 py-8 rounded-2xl backdrop-blur-sm shadow-2xl border-2" 
               style="background: linear-gradient(135deg, ${categoryInfo.color}E6, ${categoryInfo.color}CC); border-color: ${categoryInfo.color}99; min-width: 280px;">
            
            <div class="text-center mb-6">
              <div class="text-6xl mb-4">🏆</div>
              <div class="text-xs font-semibold opacity-70 tracking-wider uppercase border-b border-white border-opacity-20 pb-4 mb-5">
                Achievement
              </div>
            </div>
            
            <div class="text-lg font-medium opacity-95 mb-4">
              ${categoryInfo.name}
            </div>
            
            <div class="px-6 py-3 rounded-xl text-3xl font-mono font-bold mb-5 border border-white border-opacity-20" 
                 style="background: rgba(255, 255, 255, 0.3); backdrop-filter: blur(10px);">
              ${duration}
            </div>
            
            <div class="text-sm font-medium opacity-85">
              Exceptional Focus ✨
            </div>
          </div>
        </div>
      </div>
    `
    
    document.body.insertAdjacentHTML('beforeend', celebrationHtml)
    
    // Manually trigger confetti since we can't rely on Stimulus
    this.triggerManualConfetti(categoryInfo.color)
  }
  
  triggerManualConfetti(baseColor) {
    // Create color palette
    const colors = [
      baseColor,
      '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#54A0FF'
    ]
    
    // Initial burst
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    })
    
    // Side bursts
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.8 },
        colors: colors
      })
    }, 200)
    
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.8 },
        colors: colors
      })
    }, 400)
    
    // Additional celebration bursts
    setTimeout(() => {
      confetti({
        particleCount: 80,
        spread: 100,
        origin: { y: 0.2 },
        colors: colors
      })
    }, 800)
    
    // Continuous small bursts
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        confetti({
          particleCount: 30,
          spread: 60,
          origin: { 
            x: 0.3 + Math.random() * 0.4, 
            y: 0.5 + Math.random() * 0.3 
          },
          colors: colors
        })
      }, 1000 + i * 300)
    }
  }
  
  getCSRFToken() {
    return document.querySelector('[name="csrf-token"]')?.content ||
           document.querySelector('meta[name="csrf-token"]')?.content
  }
  
  submitStopForm(form, formData) {
    // Create a new form element to submit in background
    const submitForm = document.createElement('form')
    submitForm.method = form.method
    submitForm.action = form.action
    
    // Add all form data as hidden inputs
    for (let [key, value] of formData.entries()) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = key
      input.value = value
      submitForm.appendChild(input)
    }
    
    // Add CSRF token
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content ||
                     document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      submitForm.appendChild(csrfInput)
    }
    
    // Submit the form
    document.body.appendChild(submitForm)
    submitForm.submit()
  }

  // Handle keyboard shortcuts
  handleKeydown(event) {
    // Escape key to close modals
    if (event.key === 'Escape') {
      // Check delete modal first (higher priority)
      if (this.hasDeleteModalTarget && !this.deleteModalTarget.classList.contains('hidden')) {
        event.preventDefault()
        this.hideDeleteModal()
      } else if (this.hasModalTarget && !this.modalTarget.classList.contains('hidden')) {
        event.preventDefault()
        this.hideStopModal()
      }
    }
  }


  // Get current timer display for daily stats updates
  getCurrentDisplay() {
    if (this.hasDisplayMobileTarget) {
      return this.displayMobileTarget.textContent
    } else if (this.hasDisplayDesktopTarget) {
      return this.displayDesktopTarget.textContent
    }
    return "0:00"
  }

  // Check if celebrations are enabled for the current user
  async isCelebrationsEnabled() {
    try {
      // Check if there's a way to get this from the page data first
      const celebrationsMetaTag = document.querySelector('meta[name="user-celebrations"]')
      if (celebrationsMetaTag) {
        return celebrationsMetaTag.content === 'true'
      }
      
      // Fallback: default to true for now
      return true
    } catch (error) {
      console.warn('Could not determine celebrations setting, defaulting to enabled:', error)
      return true
    }
  }
}